/**
 * Leviathan - Delivery Programs Page
 * Quasar Prime LLC
 *
 * Manage delivery programs (Keep-Full, Will-Call, Scheduled)
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  Plus,
  Loader2,
  Search,
  Fuel,
  Gauge,
  Calendar,
  Phone,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface DeliveryProgram {
  id: string;
  customerId: string;
  customerName: string;
  locationId: string;
  locationName: string;
  locationAddress: string;
  programType: 'KEEP_FULL' | 'WILL_CALL' | 'SCHEDULED';
  productId: string;
  productName: string;
  tankCapacityGallons: number;
  currentTankLevel: number | null;
  triggerPercentage: number | null;
  targetPercentage: number | null;
  scheduleFrequency: string | null;
  scheduleDayOfWeek: number | null;
  scheduleDayOfMonth: number | null;
  lastDeliveryDate: string | null;
  nextScheduledDate: string | null;
  estimatedGallonsNeeded: number | null;
  isActive: boolean;
}

interface DashboardStats {
  totalPrograms: number;
  activePrograms: number;
  keepFullPrograms: number;
  willCallPrograms: number;
  scheduledPrograms: number;
  programsNeedingDelivery: number;
  scheduledDueThisWeek: number;
}

const PROGRAM_TYPE_CONFIG = {
  KEEP_FULL: {
    label: 'Keep Full',
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50',
    icon: Gauge,
    description: 'Auto-deliver when tank level drops below trigger',
  },
  WILL_CALL: {
    label: 'Will Call',
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/50',
    icon: Phone,
    description: 'Customer calls when delivery needed',
  },
  SCHEDULED: {
    label: 'Scheduled',
    color: 'bg-purple-500/20 text-purple-400 border-purple-500/50',
    icon: Calendar,
    description: 'Fixed recurring delivery schedule',
  },
};

const FREQUENCY_LABELS: Record<string, string> = {
  WEEKLY: 'Weekly',
  BIWEEKLY: 'Bi-weekly',
  MONTHLY: 'Monthly',
  CUSTOM: 'Custom',
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function DeliveryPrograms() {
  const { accessToken } = useAuth();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('true');
  const [showNewModal, setShowNewModal] = useState(false);

  // Fetch dashboard stats
  const { data: statsData } = useQuery({
    queryKey: ['delivery-programs-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/delivery-programs/dashboard-stats`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch stats');
      return res.json();
    },
  });

  const stats: DashboardStats = statsData?.stats || {
    totalPrograms: 0,
    activePrograms: 0,
    keepFullPrograms: 0,
    willCallPrograms: 0,
    scheduledPrograms: 0,
    programsNeedingDelivery: 0,
    scheduledDueThisWeek: 0,
  };

  // Fetch programs needing delivery
  const { data: needingDeliveryData } = useQuery({
    queryKey: ['programs-needing-delivery'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/delivery-programs/needing-delivery`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch');
      return res.json();
    },
  });

  const programsNeedingDelivery: DeliveryProgram[] = needingDeliveryData?.programs || [];

  // Fetch all programs
  const { data, isLoading } = useQuery({
    queryKey: ['delivery-programs', search, typeFilter, activeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (typeFilter) params.append('programType', typeFilter);
      if (activeFilter) params.append('isActive', activeFilter);

      const res = await fetch(`${API_URL}/api/delivery-programs?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) throw new Error('Failed to fetch programs');
      return res.json();
    },
  });

  let programs: DeliveryProgram[] = data?.programs || [];

  // Client-side search filter
  if (search) {
    const searchLower = search.toLowerCase();
    programs = programs.filter(
      (p) =>
        p.customerName.toLowerCase().includes(searchLower) ||
        p.locationName.toLowerCase().includes(searchLower) ||
        p.productName.toLowerCase().includes(searchLower)
    );
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getTankLevelDisplay = (program: DeliveryProgram) => {
    if (program.currentTankLevel === null) return null;

    const level = program.currentTankLevel;
    const trigger = program.triggerPercentage || 0;
    const isBelowTrigger = level <= trigger;

    return (
      <div className="flex items-center gap-2">
        <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isBelowTrigger ? 'bg-red-500' : level < 50 ? 'bg-yellow-500' : 'bg-emerald-500'
            }`}
            style={{ width: `${Math.min(100, level)}%` }}
          />
        </div>
        <span className={`text-sm ${isBelowTrigger ? 'text-red-400 font-semibold' : 'text-slate-400'}`}>
          {level}%
        </span>
      </div>
    );
  };

  const getScheduleDisplay = (program: DeliveryProgram) => {
    if (program.programType !== 'SCHEDULED' || !program.scheduleFrequency) return null;

    let scheduleText = FREQUENCY_LABELS[program.scheduleFrequency] || program.scheduleFrequency;

    if (program.scheduleFrequency === 'WEEKLY' && program.scheduleDayOfWeek !== null) {
      scheduleText += ` (${DAY_NAMES[program.scheduleDayOfWeek] || 'Day ' + program.scheduleDayOfWeek})`;
    } else if (program.scheduleFrequency === 'MONTHLY' && program.scheduleDayOfMonth !== null) {
      scheduleText += ` (${program.scheduleDayOfMonth}${getOrdinalSuffix(program.scheduleDayOfMonth)})`;
    }

    return scheduleText;
  };

  const getOrdinalSuffix = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0] || 'th';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Delivery Programs</h1>
          <p className="text-slate-400 mt-1">
            Manage Keep-Full, Will-Call, and Scheduled delivery programs
          </p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-slate-900 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Program
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active Programs</p>
              <p className="text-2xl font-bold text-slate-100">{stats.activePrograms}</p>
            </div>
            <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Needing Delivery</p>
              <p className="text-2xl font-bold text-red-400">{stats.programsNeedingDelivery}</p>
            </div>
            <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Scheduled This Week</p>
              <p className="text-2xl font-bold text-purple-400">{stats.scheduledDueThisWeek}</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Program Types</p>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-emerald-400 text-sm font-medium">{stats.keepFullPrograms} KF</span>
                <span className="text-blue-400 text-sm font-medium">{stats.willCallPrograms} WC</span>
                <span className="text-purple-400 text-sm font-medium">{stats.scheduledPrograms} SC</span>
              </div>
            </div>
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-amber-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Alerts - Programs Needing Delivery */}
      {programsNeedingDelivery.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <h3 className="font-semibold text-red-400">
              {programsNeedingDelivery.length} Program{programsNeedingDelivery.length > 1 ? 's' : ''} Below Trigger Level
            </h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {programsNeedingDelivery.slice(0, 6).map((program) => (
              <div
                key={program.id}
                className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3"
              >
                <div className="text-slate-100 font-medium">{program.customerName}</div>
                <div className="text-slate-400 text-sm">{program.locationName}</div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-slate-500 text-sm">{program.productName}</span>
                  <span className="text-red-400 font-semibold">
                    {program.currentTankLevel}% / {program.triggerPercentage}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by customer, location, or product..."
            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
          />
        </div>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
        >
          <option value="">All Types</option>
          <option value="KEEP_FULL">Keep Full</option>
          <option value="WILL_CALL">Will Call</option>
          <option value="SCHEDULED">Scheduled</option>
        </select>

        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-lg text-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
        >
          <option value="true">Active Only</option>
          <option value="false">Inactive Only</option>
          <option value="">All Programs</option>
        </select>
      </div>

      {/* Programs Table */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : programs.length === 0 ? (
          <div className="text-center py-12">
            <Fuel className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-4">No delivery programs found</p>
            <button
              onClick={() => setShowNewModal(true)}
              className="text-amber-400 hover:text-amber-300"
            >
              Create your first program
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-900/50 border-b border-slate-700">
              <tr>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Customer / Location
                </th>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Product
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Type
                </th>
                <th className="text-left px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Tank Level / Schedule
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Tank Capacity
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Last Delivery
                </th>
                <th className="text-center px-4 py-3 text-slate-500 text-xs uppercase tracking-wider">
                  Next Due
                </th>
              </tr>
            </thead>
            <tbody>
              {programs.map((program) => {
                const typeConfig = PROGRAM_TYPE_CONFIG[program.programType];
                const TypeIcon = typeConfig.icon;

                return (
                  <tr
                    key={program.id}
                    className="border-b border-slate-700/50 hover:bg-slate-800/50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="text-slate-100 font-medium">{program.customerName}</div>
                      <div className="text-slate-500 text-sm">{program.locationName}</div>
                      {program.locationAddress && (
                        <div className="text-slate-600 text-xs">{program.locationAddress}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-amber-400 font-medium">{program.productName}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded ${typeConfig.color}`}
                      >
                        <TypeIcon className="w-3 h-3" />
                        {typeConfig.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {program.programType === 'KEEP_FULL' ? (
                        <div>
                          {getTankLevelDisplay(program)}
                          {program.triggerPercentage !== null && (
                            <div className="text-slate-500 text-xs mt-1">
                              Trigger: {program.triggerPercentage}% | Target: {program.targetPercentage}%
                            </div>
                          )}
                        </div>
                      ) : program.programType === 'SCHEDULED' ? (
                        <div className="text-slate-300 text-sm">
                          {getScheduleDisplay(program)}
                        </div>
                      ) : (
                        <div className="text-slate-500 text-sm italic">On-demand</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="text-slate-100">
                        {program.tankCapacityGallons.toLocaleString()} gal
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-400 text-sm">
                      {formatDate(program.lastDeliveryDate)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {program.nextScheduledDate ? (
                        <div className="text-purple-400 text-sm">
                          {formatDate(program.nextScheduledDate)}
                        </div>
                      ) : program.programType === 'KEEP_FULL' &&
                        program.currentTankLevel !== null &&
                        program.triggerPercentage !== null &&
                        program.currentTankLevel <= program.triggerPercentage ? (
                        <span className="text-red-400 text-sm font-semibold">NOW</span>
                      ) : (
                        <span className="text-slate-500 text-sm">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* New Program Modal Placeholder */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold text-slate-100 mb-4">New Delivery Program</h2>
            <p className="text-slate-400 mb-6">
              Program creation form will be implemented in a future update.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowNewModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
